import { useEffect, useId, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Icon } from "./Icon";
import { useI18n } from "../lib/i18n";
import {
  annualSaving,
  effectivePrice,
  fetchIntroOffer,
  fetchPlans,
  formatAmount,
  preferredCurrency,
  register,
  sendPasswordReset,
  signIn,
  type AuthProblem,
  type IntroOffer,
  type Plan,
} from "../lib/account";
import { googleSignInEnabled, startGoogleSignIn } from "../lib/auth";
import { suggestEmail } from "../lib/onboarding";

/**
 * Joining, on the page somebody is already looking at.
 *
 * ── What this replaces ──────────────────────────────────────────────────────
 * First a link to /lms/login — the tuition marketplace's sign-in, at the
 * moment a parent was deciding whether to start at all. Then a form that
 * created the account and told them to check their email for a confirmation
 * that was never sent, so nobody who joined ever got in. Now an account opens
 * and the family is choosing a curriculum in the same breath.
 *
 * ── The decisions that matter for whether anyone finishes it ────────────────
 * Google first. For most families it is one tap, no password to invent, and an
 * address Google has already proved — the one sign-up with nothing to mistype.
 *
 * Then three fields. Name, email, password, and nothing else. The child's name,
 * age and level are all needed, and none of them HERE: asked now they are more
 * chances to close the tab; asked straight after, inside the app, they are the
 * beginning of the programme rather than a form.
 *
 * With no confirmation email, a mistyped address is never caught later — so
 * it is caught now, with a "did you mean" a parent can tap.
 *
 * The free week is stated before the fields, because it is the answer to the
 * question a visitor is actually asking. No card is mentioned, because none is
 * taken. The price is on the page while signed out, and it is the price they
 * would actually pay — the introductory figure while the offer is open, the
 * same numbers the paywall shows later.
 *
 * Joining is the default tab. Somebody with an account knows to look for sign
 * in; somebody without one should not have to.
 */

type Mode = "join" | "signin" | "reset";

/**
 * `?error=oauth|pending`, put there by the main site's /auth/callback when a
 * Google sign-in is cancelled or the account is on hold.
 *
 * Read ONCE, when this module loads, and then taken out of the address bar so
 * a refresh does not repeat a complaint about something already over. Module
 * scope rather than a state initialiser, because React may run an initialiser
 * twice and the second run would find the parameter already gone.
 */
const CALLBACK_ERROR: "oauth" | "pending" | null = (() => {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const value = url.searchParams.get("error");
    if (value !== "oauth" && value !== "pending") return null;
    url.searchParams.delete("error");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    return value;
  } catch {
    return null;
  }
})();

/**
 * How the password is doing, for the meter under the field: 0 nothing typed,
 * 1 still under the minimum, 2 acceptable, 3 strong. Encouragement only —
 * nothing past the eight-character minimum is ever enforced.
 */
function strengthOf(password: string): number {
  if (!password) return 0;
  if (password.length < 8) return 1;
  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((pattern) => pattern.test(password)).length;
  return variety >= 3 || password.length >= 14 ? 3 : 2;
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.05l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

export function JoinForm({
  supabase,
  onSignedIn,
}: {
  supabase: SupabaseClient;
  /** Carries the new session into the app. Resolves once the app has taken over. */
  onSignedIn: () => Promise<void> | void;
}) {
  const { t } = useI18n();
  const ids = useId();
  const [mode, setMode] = useState<Mode>("join");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [busy, setBusy] = useState<"form" | "google" | null>(null);
  /** Signed in; the app is loading the family behind this card. */
  const [entering, setEntering] = useState(false);
  const [problem, setProblem] = useState<string | null>(() =>
    CALLBACK_ERROR ? t(`auth.callback.${CALLBACK_ERROR}`) : null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [offers, setOffers] = useState<Map<string, IntroOffer>>(new Map());
  const currency = preferredCurrency();
  const google = googleSignInEnabled();

  useEffect(() => {
    let cancelled = false;
    void Promise.all([fetchPlans(supabase), fetchIntroOffer(supabase)]).then(([loaded, offered]) => {
      if (cancelled) return;
      setPlans(loaded);
      setOffers(offered);
    });
    return () => {
      cancelled = true;
    };
  }, [supabase]);

  const suggestion = useMemo(() => (emailChecked ? suggestEmail(email) : null), [email, emailChecked]);
  const strength = mode === "join" ? strengthOf(password) : 0;

  const monthly = plans.find((plan) => plan.billing_interval === "month");
  const annual = plans.find((plan) => plan.billing_interval === "year");
  const saving = annualSaving(plans, currency, offers);
  // Both plans share one cohort, so any open offer means the offer is open.
  const openOffer = [...offers.values()].find((offer) => offer.is_open);
  const monthlyPrice = monthly ? effectivePrice(monthly, currency, offers.get(monthly.code)) : null;
  const annualPrice = annual ? effectivePrice(annual, currency, offers.get(annual.code)) : null;

  function switchMode(next: Mode) {
    setMode(next);
    setProblem(null);
    setNotice(null);
  }

  function explain(problemKey: AuthProblem, detail: string) {
    return problemKey === "unknown" ? detail || t("auth.problem.unknown") : t(`auth.problem.${problemKey}`);
  }

  async function continueWithGoogle() {
    if (busy) return;
    setBusy("google");
    setProblem(null);
    setNotice(null);
    try {
      const result = await startGoogleSignIn(supabase);
      // On success the browser is already on its way to Google.
      if (!result.ok) {
        setProblem(result.error);
        setBusy(null);
      }
    } catch {
      setProblem(t("join.connectionError"));
      setBusy(null);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy("form");
    setProblem(null);
    setNotice(null);
    try {
      if (mode === "reset") {
        const result = await sendPasswordReset(supabase, email);
        if (result.ok) setNotice(t("auth.resetSent", { email: email.trim() }));
        else setProblem(explain(result.problem, result.detail));
        return;
      }

      const result =
        mode === "join"
          ? await register(supabase, { name, email, password })
          : await signIn(supabase, { email, password });

      if (result.ok) {
        if ("checkEmail" in result) {
          // Only when the project requires confirmation again — see register().
          setNotice(t("join.checkEmailCopy", { email: email.trim() }));
          setMode("signin");
          return;
        }
        setEntering(true);
        await onSignedIn();
        return;
      }

      // An address already in use is not an error so much as the wrong door.
      // Move them to the right one, with what they typed still in the fields —
      // most people re-type the password they already have.
      if (result.problem === "exists") {
        setMode("signin");
        setNotice(t("auth.exists"));
        return;
      }
      setProblem(explain(result.problem, result.detail));
    } catch {
      setProblem(t("join.connectionError"));
    } finally {
      setBusy(null);
    }
  }

  if (entering) {
    return (
      <div className="join-card glass-card auth-entering" role="status" aria-live="polite">
        <span className="auth-spinner" aria-hidden="true" />
        <strong>{t("auth.entering")}</strong>
        <p>{t("auth.enteringHint")}</p>
      </div>
    );
  }

  const passwordId = `${ids}-password`;

  return (
    <div className="join-card glass-card auth-card">
      {mode === "reset" ? (
        <div className="auth-reset-head">
          <button type="button" className="link-button" onClick={() => switchMode("signin")}>
            <span className="auth-back-arrow" aria-hidden="true">
              <Icon name="arrow" size={15} />
            </span>
            {t("auth.backToSignIn")}
          </button>
          <h2>{t("auth.resetTitle")}</h2>
          <p>{t("auth.resetCopy")}</p>
        </div>
      ) : (
        <div className="join-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "join"}
            className={mode === "join" ? "active" : ""}
            onClick={() => switchMode("join")}
          >
            {t("join.tabJoin")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "signin"}
            className={mode === "signin" ? "active" : ""}
            onClick={() => switchMode("signin")}
          >
            {t("join.tabSignIn")}
          </button>
        </div>
      )}

      {mode === "join" && (
        <p className="join-promise">
          <Icon name="spark" size={15} /> {t("join.freeWeek")}
        </p>
      )}

      {google && mode !== "reset" && (
        <>
          <button
            type="button"
            className="auth-google"
            onClick={() => void continueWithGoogle()}
            disabled={busy !== null}
          >
            {busy === "google" ? <span className="auth-spinner small" aria-hidden="true" /> : <GoogleMark />}
            {busy === "google" ? t("auth.googleOpening") : t("auth.google")}
          </button>
          <div className="auth-divider" aria-hidden="true">
            <span>{t("auth.or")}</span>
          </div>
        </>
      )}

      {notice && (
        <p className="auth-notice" role="status">
          <Icon name="check" size={15} /> {notice}
        </p>
      )}

      <form onSubmit={submit} className="join-form professional-form">
        {mode === "join" && (
          <label>
            <span>{t("join.name")}</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoComplete="name"
              placeholder={t("join.namePlaceholder")}
            />
          </label>
        )}
        <label>
          <span>{t("join.email")}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={() => setEmailChecked(true)}
            required
            autoComplete={mode === "join" ? "email" : "username"}
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
          />
        </label>
        {suggestion && (
          <button type="button" className="auth-suggest" onClick={() => setEmail(suggestion)}>
            {t("auth.didYouMean", { email: suggestion })}
          </button>
        )}

        {mode !== "reset" && (
          <div className="auth-password">
            <div className="auth-password-head">
              <label htmlFor={passwordId}>{t("join.password")}</label>
              {mode === "signin" && (
                <button type="button" className="link-button" onClick={() => switchMode("reset")}>
                  {t("auth.forgot")}
                </button>
              )}
            </div>
            <div className="auth-password-field">
              <input
                id={passwordId}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={mode === "join" ? 8 : undefined}
                autoComplete={mode === "join" ? "new-password" : "current-password"}
              />
              <button
                type="button"
                className="auth-eye"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                aria-pressed={showPassword}
              >
                <Icon name="eye" size={17} />
              </button>
            </div>
            {mode === "join" && (
              <div className="auth-strength" data-level={strength} aria-live="polite">
                <span className="auth-strength-bars" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <small>{strength === 0 ? t("auth.passwordHint") : t(`auth.strength.${strength}`)}</small>
              </div>
            )}
          </div>
        )}

        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}

        <button className="primary" disabled={busy !== null} type="submit">
          {busy === "form" ? (
            <>
              <span className="auth-spinner small light" aria-hidden="true" /> {t("join.working")}
            </>
          ) : (
            <>
              {mode === "join" ? t("join.start") : mode === "signin" ? t("boot.signIn") : t("auth.resetSend")}
              <Icon name="arrow" />
            </>
          )}
        </button>
      </form>

      {mode === "join" && monthlyPrice && (
        <p className="join-price">
          {t("join.afterTrial", {
            monthly: formatAmount(monthlyPrice.amount, currency),
            annual: annualPrice ? formatAmount(annualPrice.amount, currency) : "",
          })}
          {saving ? <strong> · {t("join.monthsFree", { n: saving })}</strong> : null}
        </p>
      )}

      {/* The standard price, struck through, and how many places are left.
          Both are facts a visitor can check against the paywall they will see
          later, which is the only reason a countdown like this is honest. */}
      {mode === "join" && openOffer && monthlyPrice?.wasAmount != null && (
        <p className="join-offer">
          <Icon name="spark" size={14} />
          <span>
            {t("join.introOpen", {
              remaining: openOffer.remaining,
              limit: openOffer.intro_limit ?? 0,
            })}{" "}
            <span className="join-was">
              {t("join.introWas", {
                monthly: formatAmount(monthlyPrice.wasAmount, currency),
                annual: annualPrice?.wasAmount != null ? formatAmount(annualPrice.wasAmount, currency) : "",
              })}
            </span>
          </span>
        </p>
      )}
    </div>
  );
}
